import wwwApconCom from '../../../www.apcon.com';
import {
  NetworkWideVisibilityIcon,
  OptimizePerformanceIcon,
} from '../../../assets/icons';
import {
  BadgeCheckIcon,
  ChartSquareBarIcon,
  LockClosedIcon,
  BriefcaseIcon,
  CalendarIcon,
  NewspaperIcon,
} from '@heroicons/react/solid';

const SoftwareContent = {
  suite: {
    id: 1,
    name: 'IntellaView Platform',
    description:
      'The IntellaView Platform is a comprehensive solution that includes IntellaView User Interface, IntellaView Enterprise, and IntellaView Mobile.',
    url: wwwApconCom.intellaview.DEFAULT,
    image: {
      filename: 'intellaview-logo-no-chassis',
      alt: 'An image of the IntellaView Platform',
      cover: true,
    },
  },
  Products: [
    {
      id: 1,
      name: 'IntellaView User Interface',
      description:
        'Onboard browser-based IntellaView management software that lets you view your entire system to build, view, and save connections to your IntellaView platform.',
      url: wwwApconCom.software.intellaview,
      image: {
        filename: 'intellaview-gui-thumbnail',
        alt: 'An image of the IntellaView User Interface dashboard',
      },
    },
    {
      id: 2,
      name: 'IntellaView Enterprise',
      description:
        'Our most advanced software easily creates and edits multi-switch connections from any linked device through a user-friendly design and customizable dashboard.',
      url: wwwApconCom.software.enterprise,
      image: {
        filename: 'enterprise-tile-image',
        alt: 'An image of the Enterprise dashboard',
      },
    },
    {
      id: 3,
      name: 'Enterprise Lite',
      description:
        'Our most advanced software easily creates and edits multi-switch connections from any linked device through a user-friendly design and customizable dashboard.',
      url: wwwApconCom.intellaview.enterpriseLite,
      image: {
        filename: 'enterprise-lite-tile-image',
        alt: 'An image of the Enterprise dashboard',
      },
      isNew: true,
    },
    {
      id: 4,
      name: 'IntellaView Mobile',
      description:
        'Gain immediate access to address urgent alerts or configure IntellaView blades and chassis using any iOS or Android device through the mobile app for on-the-go switch management.',
      url: wwwApconCom.software.intellaviewMobile.DEFAULT,
      image: {
        filename: 'intellaview-mobile-navbar',
        alt: 'An image of the IntellaView Mobile dashboard',
      },
    },
    {
      id: 5,
      name: 'Legacy Products',
      description:
        'View documentation for our legacy and end-of-sale products. While we no longer sell or support these products, documentation remains available for reference.',
      url: wwwApconCom.legacyProductsPolicy,
      image: {
        filename: 'intellaflex-xr-family-photo-1280w-630h',
        alt: 'An image of the Legacy Products',
      },
    },
  ],
  NameOnlyProducts: [
    {
      id: 1,
      name: 'IntellaTap-VM',
      description:
        'Intellatap-VM offers organizations the ability to send mirrored, filtered, and aggregated VM traffic to your monitoring and security tools — optimizing them and lengthening their lifespans.',
      url: wwwApconCom.software.intellatapVm,
      image: {
        filename: 'inteallatap-vm-thumbnail',
        alt: 'An image of intellatap-vm',
      },
    },
    {
      id: 2,
      name: 'IntellaCloud',
      description:
        'IntellaCloud captures packet-level network traffic from instances you want to monitor and can be configured for elastic monitoring using subnet or instance tagging.',
      url: wwwApconCom.software.intellacloud,
      image: {
        filename: 'intellacloud-connections-overview-1600w-900h',
        alt: 'An image of the Enterprise dashboard',
      },
      endOfLife: false,
      endOfSale: true,
    },
    {
      id: 3,
      name: 'TITAN',
      description: '',
      url: wwwApconCom.software.titan,
      image: {
        filename: 'titan-dashboard-1921w-1081h',
        alt: 'An image of the Enterprise dashboard',
      },
      endOfLife: false,
      endOfSale: true,
    },
    {
      id: 4,
      name: 'WebXR',
      description: '',
      url: wwwApconCom.software.webxr,
      image: {
        filename: 'intellacloud-connections-overview-1600w-900h',
        alt: 'An image of the Enterprise dashboard',
      },
      endOfLife: false,
      endOfSale: true,
    },
  ],
};

const HardwareContent = {
  Products: [
    {
      name: 'IntellaView Hardware',
      image: {
        filename: 'intellaview-family-photo-1280w-630h-mk-iii',
        alt: 'IntellaView Image',
      },
      url: wwwApconCom.hardware.intellaview.DEFAULT,
      description:
        'A next-generation 400G hybrid visibility platform accommodating an ever-growing number of users, data, and applications.',
      resourceTitle: 'The IntellaView Platform',
    },
    {
      name: 'ApconTap',
      image: {
        filename: 'apcon-tap-navbar',
        alt: 'ApconTap Image',
      },
      url: wwwApconCom.hardware.networkTaps.apconTap,
      description:
        'Our TAPs fit in the same datacenter rack near any APCON intelligent network monitoring switch.',
      resourceTitle: 'ApconTap Specifications',
    },
    {
      name: 'IntellaFlex XR',
      image: {
        filename: 'intellaflex-xr-family-photo-1920w-1080h-new',
        alt: 'ApconTap Image',
      },
      url: wwwApconCom.hardware.intellaflexXr.DEFAULT,
      description:
        'IntellaFlex XR offers the most scalable architecture available on the market.',
      resourceTitle: 'Network Security and Visibility Solutions',
      endOfLife: false,
      endOfSale: true,
    },
  ],
};

const SolutionsContent = {
  LearnMore: [
    {
      id: 1,
      name: 'Network-wide Packet Visibility',
      icon: <NetworkWideVisibilityIcon aria-hidden="true" />,
      description:
        'Collect and send data to monitoring, performance, and security tools for faster insights.',
      url: wwwApconCom.solutions.networkVisibility,
    },
    {
      id: 2,
      name: 'Optimize Network Performance',
      icon: <OptimizePerformanceIcon aria-hidden="true" />,
      description:
        "Improve network performance and reliability with APCON's tools and techniques.",

      url: wwwApconCom.solutions.networkMonitoring,
    },
    {
      id: 3,
      name: 'Enhance Network Security',
      icon: <LockClosedIcon aria-hidden="true" />,
      description:
        'Prevent breaches and ransomware and resolve problems faster with real time activity data.',

      url: wwwApconCom.solutions.networkSecurity,
    },
    {
      id: 4,
      name: 'Empower Monitoring',
      icon: <ChartSquareBarIcon aria-hidden="true" />,
      description:
        'Ensure the tools you rely on perform at optimal levels as your network fabric expands.',

      url: wwwApconCom.solutions.empowerMonitoring,
    },
    {
      id: 5,
      name: 'Ensure Regulatory Compliance',
      icon: <BadgeCheckIcon aria-hidden="true" />,

      url: wwwApconCom.solutions.networkDataCompliance,
    },
  ],
};

const CompanyContent = {
  Contacts: [
    {
      name: 'Oregon',
      address: '9255 SW Pioneer Ct Wilsonville, OR 97070',
      phone: '503-682-4050',
    },
    {
      name: 'Texas',
      address: '4351 Mapleshade Ln Plano, TX 75093',
      phone: '972-372-0198',
    },
    {
      name: 'General Questions',
      address: 'info@apcon.com',
      phone: '1-800-624-6808',
    },
    { name: 'PR Inquiries', address: 'pr@apcon.com', phone: '' },
  ],
  SubNavigation: [
    {
      name: 'News',
      href: wwwApconCom.newsroom,
      icon: <NewspaperIcon />,
      current: false,
    },
    {
      name: 'Blog',
      href: wwwApconCom.blog,
      icon: <NewspaperIcon />,
      current: false,
    },
    {
      name: 'Events',
      href: wwwApconCom.events,
      icon: <CalendarIcon />,
      current: false,
    },
    {
      name: 'Careers',
      href: wwwApconCom.careers,
      icon: <BriefcaseIcon />,
      current: false,
    },
  ],
};
export { SoftwareContent, HardwareContent, SolutionsContent, CompanyContent };
